import clsx from "clsx";
import { useUIStore } from "../../store/uiStore";
import { hasUsableCoordinates } from "../../utils/dayMapPois";
import { Button } from "../Button";
import type { DayAccommodationContext } from "./types";

interface AccommodationBannerProps {
  date: string; // YYYY-MM-DD
  accommodation: DayAccommodationContext;
  isReadOnly: boolean;
  onShowOnMap?: (poiId: string) => void;
}

/**
 * Column footer: where you sleep tonight. Palette-tinted banner with a
 * "Night x/y" badge, a wake→sleep gradient on hotel-swap days, and a muted
 * empty state. Click opens the accommodation picker (ported from the old
 * kanban SleepIndicator dropdown) writing `setSleep(date, poiId)`.
 */
export function AccommodationBanner({
  date,
  accommodation,
  isReadOnly,
  onShowOnMap,
}: AccommodationBannerProps) {
  const openAccommodationPicker = useUIStore((s) => s.openAccommodationPicker);

  const { sleepPoi, sleepPalette, wakePoi, wakePalette, isSwap, nightCount } =
    accommodation;

  const bannerStyle = sleepPoi
    ? isSwap && wakePalette && sleepPalette
      ? {
          background: `linear-gradient(100deg, ${wakePalette.header} 0%, ${wakePalette.header} 38%, ${sleepPalette.header} 62%, ${sleepPalette.header} 100%)`,
          borderColor: sleepPalette.border,
          color: sleepPalette.text,
        }
      : sleepPalette
        ? {
            background: sleepPalette.header,
            borderColor: sleepPalette.border,
            color: sleepPalette.text,
          }
        : undefined
    : undefined;

  const showOnMapEnabled =
    onShowOnMap && sleepPoi && hasUsableCoordinates(sleepPoi);

  return (
    <div
      className={clsx(
        "flex min-w-0 flex-1 items-center rounded-lg border transition-shadow",
        sleepPoi
          ? "hover:shadow-md"
          : "border-dashed border-border bg-subtle text-tertiary",
        isReadOnly && "hover:shadow-none",
      )}
      style={bannerStyle}
    >
      <Button
        variant="unstyled"
        onClick={() => !isReadOnly && openAccommodationPicker(date)}
        disabled={isReadOnly}
        aria-label={
          sleepPoi
            ? `Tonight at ${sleepPoi.name} — change accommodation`
            : "Set tonight's accommodation"
        }
        className={clsx(
          "flex min-w-0 flex-1 items-center gap-1.5 px-2.5 py-1.5 text-left text-xs font-bold",
          sleepPoi ? "cursor-pointer" : "font-semibold text-tertiary",
          isReadOnly && "cursor-default",
        )}
      >
        <span
          className="material-symbols-outlined text-sm leading-none"
          aria-hidden
        >
          {sleepPoi ? (isSwap ? "swap_horiz" : "hotel") : "hotel"}
        </span>
        <span className="truncate">
          {sleepPoi
            ? isSwap && wakePoi
              ? `${wakePoi.name} → ${sleepPoi.name}`
              : sleepPoi.name
            : "No accommodation"}
        </span>
        {nightCount && (
          <span className="ml-auto whitespace-nowrap rounded bg-white/65 px-1.5 py-px text-2xs font-extrabold">
            Night {nightCount.current}/{nightCount.total}
          </span>
        )}
      </Button>
      {showOnMapEnabled && (
        <Button
          variant="icon"
          size="sm"
          icon="location_searching"
          className="mr-1 shrink-0 text-current hover:bg-white/40 hover:text-current"
          aria-label={`Show ${sleepPoi.name} on map`}
          title="Show on map"
          onClick={() => onShowOnMap(sleepPoi.id)}
        />
      )}
    </div>
  );
}
